import { BusyWatcher, ScreenBase, watchBusy } from "@frui.ts/screens";
import type ProductDetailContext from "models/productDetailContext";
import type { ILocalizationService, INotificationService } from "@emanprague/shared-services";
import { SeverityLevel } from "@emanprague/shared-services";
import type { interfaces } from "inversify";
import { bound } from "@frui.ts/helpers";
import { action, computed, observable, runInAction } from "mobx";
import { attachAutomaticValidator, validate } from "@frui.ts/validation";
import SupplyPointsRepository from "repositories/supplyPointsRepository";
import { unwrapErrorMessage } from "repositories/helpers";
import EnumsService from "services/enumsService";
import type { SupplyPointError } from "helpers/supplyPointHelper";
import { createSupplyPointsWithIcons } from "helpers/supplyPointHelper";
import DataSyncService from "services/dataSyncService";
import ReqChangeInvoiceFormat from "entities/reqChangeInvoiceFormat";

export default class ModalInvoiceFormatViewModel extends ScreenBase {
  static navigationName = "invoice_format";

  navigationName = ModalInvoiceFormatViewModel.navigationName;
  busyWatcher = new BusyWatcher();

  @observable errorMessage?: string;
  @observable data: ReqChangeInvoiceFormat;
  @observable supplyPointsErrors: SupplyPointError[] = [];

  constructor(
    private productDetailContext: ProductDetailContext,
    public localization: ILocalizationService,
    private supplyPointRepository: SupplyPointsRepository,
    private enumsService: EnumsService,
    private dataService: DataSyncService,
    private notificationService: INotificationService
  ) {
    super();

    const account = productDetailContext.supplyPoint?.account;
    if (!account) {
      throw new Error("Supply point detail is not loaded");
    }

    this.data = new ReqChangeInvoiceFormat();
    this.data.invoice_short_format = !!account.invoiceShortFormat;
    this.data.supplyPointIds = this.currentSupplyPoints.map(i => i.id);

    attachAutomaticValidator(this.data, ReqChangeInvoiceFormat.ValidationRules);

    this.name = this.translate("invoice_format");
  }

  @action.bound
  @watchBusy
  async confirmChange() {
    this.errorMessage = undefined;
    this.supplyPointsErrors = [];

    if (!validate(this.data)) {
      return;
    }

    const response = await this.supplyPointRepository.changeInvoiceFormat(this.productDetailContext.partnerId, this.data);

    if (response.success) {
      runInAction(() => (this.supplyPointsErrors = response.payload.supplyPoints.filter(item => !item.status)));
      if (this.supplyPointsErrors.length === 0) {
        this.notificationService.addNotification(this.translateGeneral("request_sent_success"), SeverityLevel.success);
        this.requestClose();
      }
    } else {
      runInAction(() => (this.errorMessage = unwrapErrorMessage(response.payload)));
    }
  }

  @computed
  get currentSupplyPoints() {
    const currentAccountId = this.productDetailContext.supplyPointCore.accountId;
    return this.dataService.supplyPoints.filter(item => item.accountId === currentAccountId && item.contract.active);
  }

  @computed
  get currentSupplyPointsWithIcons() {
    return createSupplyPointsWithIcons(this.enumsService, this.currentSupplyPoints, "grey", this.supplyPointsErrors);
  }

  @computed
  get additionalSupplyPointsWithIcons() {
    return createSupplyPointsWithIcons(
      this.enumsService,
      this.dataService.supplyPoints.filter(
        item => !this.currentSupplyPoints.some(selectedItem => selectedItem.id === item.id) && item.contract.active
      ),
      "grey",
      this.supplyPointsErrors
    );
  }

  @bound translate(key: string) {
    return this.localization.translateGeneral(`products.options_deposit.${key}`);
  }

  @bound translateGeneral(key: string, placeholders?: Record<string, string>) {
    return this.localization.translateGeneral(`general.${key}`, placeholders);
  }

  static Factory({ container }: interfaces.Context) {
    return (productDetailContext: ProductDetailContext) => {
      return new ModalInvoiceFormatViewModel(
        productDetailContext,
        container.get("ILocalizationService"),
        container.get(SupplyPointsRepository),
        container.get(EnumsService),
        container.get(DataSyncService),
        container.get("INotificationService")
      );
    };
  }
}
